import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  PhoneInput,
  isValidIndianMobile,
  normalizeIndianMobile,
} from '@/components/ui/phone-input';
import { updateProfile } from '@shared/api/auth';
import { deleteAvatar, uploadAvatar } from '@shared/api/users';
import { updateAssociateProfile } from '@shared/api/associates';
import { apiErrorMessage } from '@shared/api/client';
import { useMe, ME_QUERY_KEY } from '@shared/hooks/use-me';
import {
  useAssociateProfile,
  ASSOCIATE_PROFILE_KEY,
} from '@shared/hooks/use-associate';
import { fullName } from '@/lib/format';
import { getStates, getDistricts, getTaluks } from '@shared/api/services';
import { CustomSelect } from '@/components/ui/custom-select';

const PROFESSION_LABELS: Record<string, string> = {
  LAWYER: 'Lawyer',
  CHARTERED_ACCOUNTANT: 'Chartered Accountant',
  COMPANY_SECRETARY: 'Company Secretary',
  COST_MANAGEMENT_ACCOUNTANT: 'Cost & Management Accountant',
  DOCUMENT_WRITER: 'Document Writer',
  TAX_CONSULTANT: 'Tax Consultant',
  OTHER_DEGREE_HOLDER: 'Other / Degree Holder',
};

export function AssociateProfilePage() {
  const { data: me } = useMe();
  const { data: profile } = useAssociateProfile();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');

  const [outletName, setOutletName] = useState('');
  const [pincode, setPincode] = useState('');
  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  const [taluk, setTaluk] = useState('');
  const [village, setVillage] = useState('');
  const [landmark, setLandmark] = useState('');
  const [street, setStreet] = useState('');

  const [statesList, setStatesList] = useState<string[]>([]);
  const [districtsList, setDistrictsList] = useState<string[]>([]);
  const [taluksList, setTaluksList] = useState<string[]>([]);

  useEffect(() => {
    getStates().then(setStatesList).catch(console.error);
  }, []);

  useEffect(() => {
    if (state) {
      getDistricts(state).then(setDistrictsList).catch(console.error);
    } else {
      setDistrictsList([]);
    }
  }, [state]);

  useEffect(() => {
    if (district) {
      getTaluks(district).then(setTaluksList).catch(console.error);
    } else {
      setTaluksList([]);
    }
  }, [district]);

  useEffect(() => {
    if (!me) return;
    setFirstName(me.firstName ?? '');
    setLastName(me.lastName ?? '');
    setMobile(normalizeIndianMobile(me.mobile));
  }, [me]);

  useEffect(() => {
    if (!profile) return;
    setOutletName(profile.outletName ?? '');
    setPincode(profile.address.pincode ?? '');
    setState(profile.address.state ?? '');
    setDistrict(profile.address.district ?? '');
    setTaluk(profile.address.taluk ?? '');
    setVillage(profile.address.village ?? '');
    setLandmark(profile.address.landmark ?? '');
    setStreet(profile.address.street ?? '');
  }, [profile]);

  const saveUser = useMutation({
    mutationFn: () =>
      updateProfile({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        mobile: mobile.trim() || undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });

  const saveAssociate = useMutation({
    mutationFn: () =>
      updateAssociateProfile({
        outletName: outletName.trim() || undefined,
        pincode: pincode.trim() || undefined,
        state: state.trim() || undefined,
        district: district.trim() || undefined,
        taluk: taluk.trim() || undefined,
        village: village.trim() || undefined,
        landmark: landmark.trim() || undefined,
        street: street.trim() || undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ASSOCIATE_PROFILE_KEY }),
  });

  const avatar = useMutation({
    mutationFn: (f: File) => uploadAvatar(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
  const removeAvatar = useMutation({
    mutationFn: () => deleteAvatar(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });

  const userDirty =
    (me?.firstName ?? '') !== firstName ||
    (me?.lastName ?? '') !== lastName ||
    normalizeIndianMobile(me?.mobile) !== mobile;

  const assocDirty =
    profile !== undefined &&
    ((profile.outletName ?? '') !== outletName ||
      (profile.address.pincode ?? '') !== pincode ||
      (profile.address.state ?? '') !== state ||
      (profile.address.district ?? '') !== district ||
      (profile.address.taluk ?? '') !== taluk ||
      (profile.address.village ?? '') !== village ||
      (profile.address.landmark ?? '') !== landmark ||
      (profile.address.street ?? '') !== street);

  async function saveAll() {
    if (userDirty) await saveUser.mutateAsync();
    if (assocDirty) await saveAssociate.mutateAsync();
  }

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      <nav className="text-sm text-gray-500">
        <Link to="/associate/transactions" className="hover:underline">
          Transactions
        </Link>
        {' / '}
        <span className="text-gray-900">Profile</span>
      </nav>
      <h1 className="mt-4 text-2xl font-semibold text-gray-900">Profile</h1>
      <p className="mt-1 text-sm text-gray-600">
        View and edit your Associate details. Profession &amp; account type are fixed at signup.
      </p>

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
        <h2 className="text-sm font-semibold text-gray-900">Basic Info</h2>
        <div className="mt-4 flex items-center gap-4">
          <Avatar url={me?.avatarUrl} name={fullName(firstName, lastName)} />
          <div className="flex-1">
            <p className="text-xs text-gray-500">JPG, PNG or WebP, under 5 MB.</p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                className="px-3 py-1.5 text-xs"
                onClick={() => fileRef.current?.click()}
                disabled={avatar.isPending}
              >
                {avatar.isPending ? 'Uploading…' : 'Upload photo'}
              </Button>
              {me?.avatarUrl ? (
                <Button
                  variant="ghost"
                  className="px-3 py-1.5 text-xs"
                  onClick={() => removeAvatar.mutate()}
                  disabled={removeAvatar.isPending}
                >
                  Remove
                </Button>
              ) : null}
            </div>
            {avatar.isError ? (
              <p className="mt-1 text-xs text-red-600">
                {apiErrorMessage(avatar.error, (avatar.error as Error)?.message)}
              </p>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) avatar.mutate(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="First name">
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name">
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
          <Field label="Email" hint="Email is fixed — re-verification required to change.">
            <Input value={me?.email ?? ''} readOnly disabled />
          </Field>
          <Field
            label="Phone number"
            hint={
              mobile && !isValidIndianMobile(mobile)
                ? 'Enter a 10-digit Indian mobile number.'
                : 'Indian numbers only.'
            }
          >
            <PhoneInput value={mobile} onChange={setMobile} />
          </Field>
        </div>
      </section>

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
        <h2 className="text-sm font-semibold text-gray-900">Professional</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {profile?.associateId ? (
            <Field label="Associate ID" hint="Your unique system-generated associate ID.">
              <Input
                value={profile.associateId}
                readOnly
                disabled
                className="font-mono font-semibold"
              />
            </Field>
          ) : null}
          <Field label="Profession" hint="Fixed at signup — contact admin to change.">
            <Input
              value={profile ? (PROFESSION_LABELS[profile.profession] ?? profile.profession) : ''}
              readOnly
              disabled
            />
          </Field>
          <Field label="Account type" hint="Fixed at signup.">
            <Input
              value={profile ? (profile.isFranchise ? 'Franchise / Outlet' : 'Associate') : ''}
              readOnly
              disabled
            />
          </Field>
          <Field label="Registration no." hint="From your professional body.">
            <Input value={profile?.registrationNo ?? ''} readOnly disabled />
          </Field>
          {profile?.isFranchise ? (
            <Field label="Outlet name">
              <Input value={outletName} onChange={(e) => setOutletName(e.target.value)} />
            </Field>
          ) : null}
        </div>
      </section>

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
        <h2 className="text-sm font-semibold text-gray-900">Address</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="State">
            <CustomSelect
              value={state}
              onChange={(selectedState) => {
                setState(selectedState);
                setDistrict('');
              }}
              options={statesList}
              placeholder="Select State"
            />
          </Field>
          <Field label="District">
            <CustomSelect
              value={district}
              onChange={(selectedDistrict) => {
                setDistrict(selectedDistrict);
                setTaluk('');
              }}
              options={districtsList}
              placeholder="Select District"
              disabled={!state}
            />
          </Field>
          <Field label="Taluk">
            {taluksList.length > 0 ? (
              <CustomSelect
                value={taluk}
                onChange={(selectedTaluk) => setTaluk(selectedTaluk)}
                options={taluksList}
                placeholder="Select Taluk"
              />
            ) : (
              <Input value={taluk} onChange={(e) => setTaluk(e.target.value)} />
            )}
          </Field>
          <Field label="PIN code">
            <Input
              inputMode="numeric"
              maxLength={6}
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </Field>
          <Field label="Village / Area">
            <Input value={village} onChange={(e) => setVillage(e.target.value)} />
          </Field>
          <Field label="Landmark">
            <Input value={landmark} onChange={(e) => setLandmark(e.target.value)} />
          </Field>
          <Field label="Street / House" className="sm:col-span-2">
            <Input value={street} onChange={(e) => setStreet(e.target.value)} />
          </Field>
        </div>
      </section>

      {saveUser.isError || saveAssociate.isError ? (
        <p className="mt-4 text-sm text-red-600">
          {apiErrorMessage(saveUser.error ?? saveAssociate.error)}
        </p>
      ) : null}

      <div className="mt-6 flex justify-end gap-3">
        <Button
          onClick={saveAll}
          disabled={
            (!userDirty && !assocDirty) ||
            saveUser.isPending ||
            saveAssociate.isPending ||
            (!!mobile && !isValidIndianMobile(mobile))
          }
        >
          {saveUser.isPending || saveAssociate.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

    </div>
  );
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
      {hint ? <div className="mt-1 text-xs text-gray-500">{hint}</div> : null}
    </label>
  );
}

function Avatar({ url, name }: { url: string | null | undefined; name: string }) {
  if (url) return <img src={url} alt="" className="h-16 w-16 rounded-full object-cover" />;
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 text-lg font-semibold text-brand-700">
      {initials || 'U'}
    </span>
  );
}
